import "./Button.scss"

import React, { FC, MouseEvent, ReactNode } from "react"
import { Link } from "react-router-dom"
import cx from "classnames"

import Spinner from "components/Spinner/Spinner"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"

export type ButtonTheme = "primary" | "secondary" | "grey" | "black" | "link" | "link-danger" | "danger" | "success" | "transparent"
type ButtonSize = "small"

export interface IButtonProps {
    className?: string
    ariaLabel?: string
    disabled?: boolean
    loading?: boolean
    icon?: ReactNode | null
    iconPosition?: "start" | "end"
    label?: ReactNode
    onClick?: Function
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] | "link"
    url?: string
    target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"]
    tabIndex?: React.ButtonHTMLAttributes<HTMLButtonElement>["tabIndex"]
    rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>["rel"]
    theme?: ButtonTheme
    size?: ButtonSize
    width?: string
    userSelect?: React.CSSProperties['userSelect']
    clamp?: 1 | 2 | 3
    title?: string
    round?: boolean
    download?: boolean
    narrow?: boolean
}

const Button: FC<React.PropsWithChildren<IButtonProps>> = ({
    className = "",
    ariaLabel,
    disabled = false,
    loading = false,
    type = "button",
    theme = "primary",
    size,
    url = "",
    label,
    width,
    icon,
    iconPosition = "start",
    onClick = () => { },
    userSelect,
    clamp,
    title,
    target,
    tabIndex,
    rel,
    round,
    download,
    narrow,
    ...others
}) => {
    let layoutClass = ""
    if (icon && label) {
        if (iconPosition === "start") {
            layoutClass = "iconAndLabel"
        } else {
            layoutClass = "labelAndIcon"
        }
    } else {
        layoutClass = "iconOrLabel"
    }

    const buttonIcon = icon ? (
        <span className="button__icon">
            {icon}
        </span>
    ) : null

    const buttonLabel = label ? (
        typeof label === "string"
            ? <FlexibleContent
                text={label}
                className="button__label"
            />
            : <span className={cx("button__label", clamp ? `clamp-${clamp}` : "")}>
                {label}
            </span>
    ) : null

    const buttonClassNames = cx([
        "button",
        `button--${theme}`,
        {
            [`button--size-${size}`]: !!size,
            "button--narrow": narrow,
            "button--disabled": disabled,
        },
        className,
        {
            "button--loading": loading,
            "button--round": round,
        }
    ])

    const buttonContent = (
        <>
            <span className={`button__content-wrapper--${layoutClass}`}>
                {buttonIcon}
                {buttonLabel}
            </span>
            {loading &&
                <Spinner size={20} />
            }
        </>
    )

    const commonPorps = {
        disabled,
        className: buttonClassNames,
        "aria-label": ariaLabel,
        style: { width, userSelect },
        title,
        tabIndex,
        ...others
    }

    if (type === "link") {
        const linkProps = {
            onClick: onClick as (e: MouseEvent<HTMLAnchorElement>) => void,
            target,
            rel,
            download,
        }
        if (url.toLowerCase().startsWith("https://")
            || url.toLowerCase().startsWith("http://")) {
            return <a
                {...commonPorps}
                {...linkProps}
                href={url}>
                {buttonContent}
            </a>
        }
        return (
            <Link
                {...commonPorps}
                {...linkProps}
                to={url}>
                {buttonContent}
            </Link>
        )
    }

    return (
        <button {...commonPorps}
            type={type}
            onClick={onClick as (e: MouseEvent<HTMLButtonElement>) => void}>
            {buttonContent}
        </button>
    )
}

export default Button
